import React from 'react';
import { CalendarOff } from 'lucide-react';
import type { AusgeschlossenesGewerk, ZeitachsenDaten } from './koordinierungLogic';

/**
 * Die Zeitachse — reine Anzeige.
 *
 * Alle Lagen kommen als Anteil zwischen 0 und 1 fertig aus `baueZeitachse`
 * (`koordinierungLogic.ts`), hier wird nur mit `%` daraus Breite. Kein
 * `Date`, keine Rechnung: das Projekt hat kein jsdom, was hier stuende, waere
 * ungeprueft.
 *
 * Aufbau wie im Plotterplan: Kalendermonate als Kopfzeile, ein Balken je
 * Gewerk. Zusaetzlich sind die Abschnitte hervorgehoben, in denen ein Gewerk
 * mit mindestens einem anderen zusammentrifft — das ist der Teil, um den es
 * hier ueberhaupt geht.
 */

interface Props {
    zeitachse: ZeitachsenDaten | null;
    ohneZeitraum: AusgeschlossenesGewerk[];
    /** Norm-Schluessel der beiden Gewerke des geoeffneten Paares. */
    hervorgehoben?: string[];
}

const prozent = (v: number): string => `${(v * 100).toFixed(4)}%`;

const tagText = (iso: string): string => {
    // Rein zeichenweise aus dem ISO-Datum. `toLocaleDateString` haenge an der
    // Zeitzone der Anzeige und koennte einen Tag danebenliegen — genau der
    // Fehler, den die Rechenlogik vermeidet.
    if (typeof iso !== 'string' || iso.length < 10) return '—';
    return `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}`;
};

export const ZeitachseAnsicht: React.FC<Props> = ({ zeitachse, ohneZeitraum, hervorgehoben }) => {
    if (!zeitachse) {
        return (
            <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center">
                <CalendarOff className="w-10 h-10 text-slate-300 mx-auto mb-3" />
                <p className="text-sm text-slate-500">
                    Kein Gewerk im Plan hat einen vollständigen Zeitraum — ohne Datum gibt es keine Zeitachse.
                </p>
                {ohneZeitraum.length > 0 && (
                    <p className="text-xs text-slate-400 mt-2">
                        Betroffen: {ohneZeitraum.map(a => a.gewerk.label).join(', ')}.
                    </p>
                )}
            </div>
        );
    }

    const merken = new Set(hervorgehoben || []);

    return (
        <div className="space-y-4">
            <div className="bg-white rounded-2xl border border-slate-200 p-4">
                <div className="flex items-baseline justify-between mb-3 gap-3 flex-wrap">
                    <p className="text-[11px] font-black text-slate-400 uppercase tracking-wide">Bauzeit</p>
                    <p className="text-xs text-slate-500">
                        {tagText(zeitachse.start)} bis {tagText(zeitachse.ende)} · {zeitachse.spanneTage} Tage
                    </p>
                </div>

                {/* Der Kasten rollt für sich: bei langer Bauzeit werden die
                    Monatsspalten sonst schmaler als ihre Beschriftung. */}
                <div className="overflow-x-auto">
                    <div className="min-w-[640px]">

                        {/* ─── Monatsraster ───────────────────────────── */}
                        <div className="flex">
                            <div className="w-44 shrink-0" />
                            <div className="relative flex-1 h-6 border-b border-slate-200">
                                {zeitachse.monate.map(m => (
                                    <div
                                        key={`${m.jahr}-${m.monat}`}
                                        className="absolute top-0 bottom-0 border-l border-slate-200 flex items-center justify-center overflow-hidden"
                                        style={{
                                            left: prozent(m.anteilVon),
                                            width: prozent(m.anteilBis - m.anteilVon),
                                        }}
                                    >
                                        <span className="text-[10px] font-bold text-slate-400 whitespace-nowrap px-1">
                                            {m.label}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* ─── Balken ─────────────────────────────────── */}
                        <div className="divide-y divide-slate-100">
                            {zeitachse.balken.map(b => {
                                const betont = merken.has(b.norm);
                                return (
                                    <div key={b.norm} className={`flex items-center ${betont ? 'bg-blue-50/60' : ''}`}>
                                        <div className="w-44 shrink-0 pr-3 py-2">
                                            <p className={`text-xs truncate ${betont ? 'font-black text-blue-800' : 'font-bold text-slate-700'}`} title={b.label}>
                                                {b.label}
                                            </p>
                                            <p className="text-[10px] text-slate-400">
                                                {tagText(b.von)} – {tagText(b.bis)}
                                            </p>
                                        </div>
                                        <div className="relative flex-1 h-9">
                                            {/* Monatslinien auch hinter den Balken, sonst
                                                schwebt der Balken ohne Bezug im Raum. */}
                                            {zeitachse.monate.map(m => (
                                                <div
                                                    key={`raster-${b.norm}-${m.jahr}-${m.monat}`}
                                                    className="absolute top-0 bottom-0 border-l border-slate-100"
                                                    style={{ left: prozent(m.anteilVon) }}
                                                />
                                            ))}
                                            <div
                                                className={`absolute top-2.5 h-4 rounded ${betont ? 'bg-blue-300' : 'bg-slate-300'}`}
                                                style={{
                                                    left: prozent(b.anteilVon),
                                                    width: prozent(Math.max(b.anteilBis - b.anteilVon, 0.002)),
                                                }}
                                            />
                                            {/* Die Überschneidungszonen liegen ÜBER dem Balken
                                                und sind der eigentliche Befund dieser Ansicht. */}
                                            {b.zonen.map(z => (
                                                <div
                                                    key={`${b.norm}-${z.von}`}
                                                    title={`Zusammentreffen mit anderen Gewerken: ${tagText(z.von)} – ${tagText(z.bis)}`}
                                                    className="absolute top-1.5 h-6 rounded bg-amber-400/80 border border-amber-500"
                                                    style={{
                                                        left: prozent(z.anteilVon),
                                                        width: prozent(Math.max(z.anteilBis - z.anteilVon, 0.002)),
                                                    }}
                                                />
                                            ))}
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-slate-500">
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-6 h-3 rounded bg-slate-300 inline-block" /> Einsatzzeit des Gewerks
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                        <span className="w-6 h-3 rounded bg-amber-400/80 border border-amber-500 inline-block" /> Zusammentreffen mit anderen Gewerken
                    </span>
                    <span>Ein gemeinsamer Kalendertag zählt bereits als Überschneidung.</span>
                </div>
            </div>

            {ohneZeitraum.length > 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
                    <p className="text-sm font-bold text-amber-900 mb-2">
                        Nicht auf der Zeitachse: {ohneZeitraum.length} Gewerk(e) ohne verwertbaren Zeitraum
                    </p>
                    <ul className="space-y-1.5">
                        {ohneZeitraum.map(a => (
                            <li key={a.gewerk.norm} className="text-xs text-amber-900">
                                <span className="font-bold">{a.gewerk.label}</span> — {a.hinweis}
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
};
